import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Alert, Form } from 'react-bootstrap';
import axios from 'axios';
import CustomRichTextEditor from './CustomRichTextEditor';

const API_URL = process.env.REACT_APP_API_URL;

const OperationModal = ({
  show,
  onHide,
  onSubmit,
  form,
  onInputChange,
  submitting,
  error,
  success,
}) => {
  const [suggestions, setSuggestions] = useState({ types: [], hospitals: [] });
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showHospitalDropdown, setShowHospitalDropdown] = useState(false);
  const typeInputRef = useRef(null);
  const hospitalInputRef = useRef(null);

  const handleNotesChange = (html) => {
    onInputChange({ target: { name: 'clinical_notes', value: html } });
  };

  useEffect(() => {
    if (show) {
      fetchSuggestions();
    }
  }, [show]);

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get(`${API_URL}operations/suggestions/unique`);
      setSuggestions({
        types: response.data.types || [],
        hospitals: response.data.hospitals || []
      });
    } catch (err) {
      console.error('Failed to fetch operation suggestions:', err);
    }
  };

  const handleTypeChange = (e) => {
    const value = e.target.value;
    onInputChange(e);
    setShowTypeDropdown(value.trim().length > 0);
  };

  const handleHospitalChange = (e) => {
    const value = e.target.value;
    onInputChange(e);
    setShowHospitalDropdown(value.trim().length > 0);
  };

  const selectType = (type) => {
    onInputChange({ target: { name: 'type', value: type } });
    setShowTypeDropdown(false);
  };

  const selectHospital = (hospital) => {
    onInputChange({ target: { name: 'hospital', value: hospital } });
    setShowHospitalDropdown(false);
  };

  const filterSuggestions = (list, query) => {
    if (!query || !query.trim()) return [];
    const lowerQuery = query.toLowerCase();
    return list.filter(item => item.toLowerCase().includes(lowerQuery)).slice(0, 10);
  };

  const filteredTypes = filterSuggestions(suggestions.types, form?.type || '');
  const filteredHospitals = filterSuggestions(suggestions.hospitals, form?.hospital || '');

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Add Operation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              <i className="fas fa-exclamation-circle me-2"></i>
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" className="mb-3">
              <i className="fas fa-check-circle me-2"></i>
              {success}
            </Alert>
          )}

          <Form.Group className="mb-3 position-relative">
            <Form.Label>Type</Form.Label>
            <Form.Control
              ref={typeInputRef}
              type="text"
              name="type"
              value={form?.type || ''}
              onChange={handleTypeChange}
              onFocus={() => {
                if ((form?.type || '').trim().length > 0) setShowTypeDropdown(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setShowTypeDropdown(false);
              }}
              autoComplete="off"
              required
              placeholder="e.g., Appendectomy"
            />
            {showTypeDropdown && filteredTypes.length > 0 && (
              <div
                className="list-group position-absolute w-100 shadow-sm"
                style={{ zIndex: 1050, maxHeight: '180px', overflowY: 'auto' }}
              >
                {filteredTypes.map((type, i) => (
                  <button
                    key={i}
                    type="button"
                    className="list-group-item list-group-item-action py-2"
                    onClick={() => selectType(type)}
                    title={type}
                  >
                    {type}
                  </button>
                ))}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="date"
              name="date"
              value={form?.date || ''}
              onChange={onInputChange}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3 position-relative">
            <Form.Label>Hospital</Form.Label>
            <Form.Control
              ref={hospitalInputRef}
              type="text"
              name="hospital"
              value={form?.hospital || ''}
              onChange={handleHospitalChange}
              onFocus={() => {
                if ((form?.hospital || '').trim().length > 0) setShowHospitalDropdown(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setShowHospitalDropdown(false);
              }}
              autoComplete="off"
              placeholder="Optional"
            />
            {showHospitalDropdown && filteredHospitals.length > 0 && (
              <div
                className="list-group position-absolute w-100 shadow-sm"
                style={{ zIndex: 1050, maxHeight: '180px', overflowY: 'auto' }}
              >
                {filteredHospitals.map((hospital, i) => (
                  <button
                    key={i}
                    type="button"
                    className="list-group-item list-group-item-action py-2"
                    onClick={() => selectHospital(hospital)}
                    title={hospital}
                  >
                    {hospital}
                  </button>
                ))}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>Clinical Notes</Form.Label>
            <CustomRichTextEditor
              value={form?.clinical_notes || ''}
              onChange={handleNotesChange}
              placeholder="Enter notes"
              style={{ minHeight: '160px' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={submitting || !(String(form?.type || '').trim()) || !(String(form?.date || '').trim())}
          >
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default OperationModal;
